// src/auth/useEmailVerification.ts
//
// Drives the "verify your email" UX for password-based accounts:
//  - sends the verification email (with our custom action URL continuation)
//  - polls Firebase to detect when the user has verified (in another tab/device)
//  - exposes a provider-aware "open inbox" deep link (Gmail search, etc.)
//
// Google-sign-in users arrive already verified, so this no-ops for them.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
	sendEmailVerification,
	reload,
	type User,
	type UserInfo,
	type ActionCodeSettings,
} from 'firebase/auth';
import { auth } from '../lib/firebase';

// After the user clicks the emailed link, Firebase can bounce them back here.
// Points at the same /auth/action page (mode=verifyEmail is handled there).
const VERIFY_CONTINUE_URL = `${window.location.origin}/auth/action`;

const POLL_MS = 4000;

export interface EmailVerificationState {
	/** Current signed-in user (or null). */
	user: User | null;
	/** True once we know the address is verified. */
	verified: boolean;
	/** True only for an unverified password account (Google users are pre-verified). */
	needsVerification: boolean;
	/** In-flight send. */
	sending: boolean;
	/** True while the background poll is checking. */
	checking: boolean;
	/** Human-readable outcome of the last send ('sent' | error text | null). */
	sendResult: 'sent' | string | null;
	/** Seconds remaining before another send is allowed (0 = ready). */
	cooldown: number;
	/** Send (or resend) the verification email. */
	sendVerification: () => Promise<void>;
	/** Force an immediate verification check (used by "I've verified" button). */
	checkNow: () => Promise<boolean>;
	/** Deep link to the user's inbox, pre-filtered to the Nisk email. */
	inboxUrl: string | null;
	/** Label for the inbox button, e.g. "Open Gmail". */
	inboxLabel: string;
}

const RESEND_COOLDOWN_S = 30;

// Provider-specific inbox deep links. The query filters to mail from our
// sending domain so the verification email is the first thing they see.
function buildInboxLink(email: string): { url: string; label: string } | null {
	const at = email.lastIndexOf('@');
	if (at < 0) return null;
	const domain = email.slice(at + 1).toLowerCase();

	// Filter for messages from the Nisk sending domain (covers noreply@ / victor@).
	const gmailQuery = encodeURIComponent('in:anywhere from:(nisk.co)');

	if (domain === 'gmail.com' || domain === 'googlemail.com') {
		return {
			url: `https://mail.google.com/mail/u/0/#search/${gmailQuery}`,
			label: 'Open Gmail',
		};
	}
	if (
		domain === 'outlook.com' ||
		domain === 'hotmail.com' ||
		domain === 'live.com' ||
		domain === 'msn.com'
	) {
		return {
			url: 'https://outlook.live.com/mail/0/',
			label: 'Open Outlook',
		};
	}
	if (domain === 'yahoo.com') {
		return { url: 'https://mail.yahoo.com/', label: 'Open Yahoo Mail' };
	}
	if (
		domain === 'icloud.com' ||
		domain === 'me.com' ||
		domain === 'mac.com'
	) {
		return {
			url: 'https://www.icloud.com/mail/',
			label: 'Open iCloud Mail',
		};
	}
	// Unknown provider — no reliable deep link.
	return null;
}

export function useEmailVerification(): EmailVerificationState {
	const [user, setUser] = useState<User | null>(auth.currentUser);
	const [verified, setVerified] = useState<boolean>(
		auth.currentUser?.emailVerified ?? false,
	);
	const [sending, setSending] = useState(false);
	const [checking, setChecking] = useState(false);
	const [sendResult, setSendResult] = useState<'sent' | string | null>(null);
	const [cooldown, setCooldown] = useState(0);

	// Keep local state in sync with auth without a second listener elsewhere.
	useEffect(() => {
		setUser(auth.currentUser);
		setVerified(auth.currentUser?.emailVerified ?? false);
	}, []);

	// Cooldown ticker.
	useEffect(() => {
		if (cooldown <= 0) return;
		const t = setInterval(() => {
			setCooldown((c) => (c > 0 ? c - 1 : 0));
		}, 1000);
		return () => clearInterval(t);
	}, [cooldown]);

	const isPasswordUser =
		user?.providerData.some((p: UserInfo) => p.providerId === 'password') ??
		false;
	const needsVerification = !!user && isPasswordUser && !verified;

	// checkNow: reload the Firebase user and re-read emailVerified. Returns the
	// fresh value so callers ("I've verified" button) can react immediately.
	const checkNow = useCallback(async (): Promise<boolean> => {
		const current = auth.currentUser;
		if (!current) return false;
		setChecking(true);
		try {
			await reload(current);
			const isVerified = current.emailVerified;
			setVerified(isVerified);
			setUser(auth.currentUser);
			return isVerified;
		} catch {
			return false;
		} finally {
			setChecking(false);
		}
	}, []);

	// Background poll: while unverified, quietly check every few seconds so the
	// banner flips to "verified" the moment they click the link in another tab.
	const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
	useEffect(() => {
		if (!needsVerification) {
			if (pollRef.current) {
				clearInterval(pollRef.current);
				pollRef.current = null;
			}
			return;
		}
		pollRef.current = setInterval(() => {
			void checkNow();
		}, POLL_MS);
		return () => {
			if (pollRef.current) {
				clearInterval(pollRef.current);
				pollRef.current = null;
			}
		};
	}, [needsVerification, checkNow]);

	const sendVerification = useCallback(async (): Promise<void> => {
		const current = auth.currentUser;
		if (!current || current.emailVerified || cooldown > 0) return;

		setSending(true);
		setSendResult(null);
		try {
			const settings: ActionCodeSettings = {
				url: VERIFY_CONTINUE_URL,
				handleCodeInApp: false,
			};
			await sendEmailVerification(current, settings);
			setSendResult('sent');
			setCooldown(RESEND_COOLDOWN_S);
		} catch (err) {
			const code = (err as { code?: string }).code ?? '';
			setSendResult(sendErrorText(code));
		} finally {
			setSending(false);
		}
	}, [cooldown]);

	const inbox = user?.email ? buildInboxLink(user.email) : null;

	return {
		user,
		verified,
		needsVerification,
		sending,
		checking,
		sendResult,
		cooldown,
		sendVerification,
		checkNow,
		inboxUrl: inbox?.url ?? null,
		inboxLabel: inbox?.label ?? 'Open email app',
	};
}

function sendErrorText(code: string): string {
	switch (code) {
		case 'auth/too-many-requests':
			return 'Too many emails sent. Please wait a bit and try again.';
		case 'auth/network-request-failed':
			return 'Network error. Check your connection and try again.';
		default:
			return 'Could not send the email. Please try again.';
	}
}
